(function initPhotoMetadata(root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.STIPhotoMetadata = api;
}(typeof window !== 'undefined' ? window : globalThis, function photoMetadataFactory() {
  'use strict';

  const EXIF_TAGS = Object.freeze([
    'Make', 'Model', 'LensModel', 'Orientation', 'ExifImageWidth', 'ExifImageHeight',
    'ImageWidth', 'ImageHeight', 'ISO', 'PhotographicSensitivity', 'ExposureTime',
    'FNumber', 'FocalLength', 'FocalLengthIn35mmFormat', 'Flash', 'WhiteBalance',
    'LightSource', 'ExposureMode', 'ExposureProgram', 'SceneCaptureType',
    'CustomRendered', 'Software',
  ]);
  const CAMERA_MIME_TYPES = new Set([
    'image/jpeg', 'image/jpg', 'image/heic', 'image/heif', 'image/avif',
  ]);
  const EDITED_PATTERN = /whatsapp|instagram|facebook|photoshop|lightroom|snapseed|screenshot|canva|wechat/i;

  function cleanString(value) {
    if (value === undefined || value === null) return undefined;
    const text = String(value).trim().replace(/[\u0000-\u001f\u007f]/g, '');
    return text ? text.slice(0, 120) : undefined;
  }

  function finiteNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
  }

  function flashFired(value) {
    if (value === undefined || value === null) return undefined;
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return (value & 1) === 1;
    const text = String(value).toLowerCase();
    if (/did not fire|not fired|no flash/.test(text)) return false;
    return /fired/.test(text);
  }

  function isNonstandard(raw) {
    const joined = [raw.ExposureMode, raw.ExposureProgram, raw.SceneCaptureType, raw.CustomRendered]
      .map(value => String(value ?? '').toLowerCase()).join(' ');
    return /manual|night|portrait|landscape|creative|custom process|hdr/.test(joined);
  }

  function project(raw) {
    const iso = finiteNumber(raw.ISO ?? raw.PhotographicSensitivity);
    const exif = {
      cameraMake: cleanString(raw.Make), cameraModel: cleanString(raw.Model),
      lensModel: cleanString(raw.LensModel), orientation: finiteNumber(raw.Orientation),
      iso, exposureTime: finiteNumber(raw.ExposureTime), fNumber: finiteNumber(raw.FNumber),
      focalLength: finiteNumber(raw.FocalLength),
      focalLength35mm: finiteNumber(raw.FocalLengthIn35mmFormat),
      flashFired: flashFired(raw.Flash), whiteBalance: cleanString(raw.WhiteBalance),
      lightSource: cleanString(raw.LightSource), exposureMode: cleanString(raw.ExposureMode),
      exposureProgram: cleanString(raw.ExposureProgram),
      sceneCaptureType: cleanString(raw.SceneCaptureType),
      customRendered: cleanString(raw.CustomRendered), software: cleanString(raw.Software),
    };
    return Object.fromEntries(Object.entries(exif).filter(([, value]) => value !== undefined));
  }

  function warningsFor(status, exif, raw) {
    const warnings = [];
    if (status === 'missing') warnings.push('exif_missing');
    if (status === 'parse_error') warnings.push('exif_parse_error');
    if (status === 'unsupported') warnings.push('exif_unsupported');
    if (!exif.cameraMake || !exif.cameraModel) warnings.push('camera_unknown');
    if (exif.flashFired === true) warnings.push('flash_fired');
    if (exif.software && EDITED_PATTERN.test(exif.software)) warnings.push('edited_or_shared_copy');
    if (isNonstandard(raw)) warnings.push('nonstandard_mode');
    return warnings;
  }

  function isFilteredNoExifError(error) {
    return error
      && error.name === 'TypeError'
      && /undefined is not iterable/.test(String(error.message || ''));
  }

  async function readPhotoMetadata(file, parser) {
    const mime = String(file && file.type || '').toLowerCase();
    if (mime && !CAMERA_MIME_TYPES.has(mime)) {
      const exif = {};
      return {
        status: 'unsupported',
        exif,
        image: { width: 0, height: 0 },
        warningCodes: warningsFor('unsupported', exif, {}),
      };
    }
    if (!parser || typeof parser.parse !== 'function') {
      const exif = {};
      return {
        status: 'parse_error',
        exif,
        image: { width: 0, height: 0 },
        warningCodes: warningsFor('parse_error', exif, {}),
      };
    }
    try {
      const raw = await parser.parse(file, EXIF_TAGS) || {};
      const exif = project(raw);
      const status = Object.keys(exif).length ? 'present' : 'missing';
      return {
        status,
        exif,
        image: { width: 0, height: 0 },
        warningCodes: warningsFor(status, exif, raw),
      };
    } catch (error) {
      const exif = {};
      if (isFilteredNoExifError(error)) {
        return {
          status: 'missing',
          exif,
          image: { width: 0, height: 0 },
          warningCodes: warningsFor('missing', exif, {}),
        };
      }
      return {
        status: 'parse_error',
        exif,
        image: { width: 0, height: 0 },
        warningCodes: warningsFor('parse_error', exif, {}),
      };
    }
  }

  function withDecodedDimensions(result, width, height) {
    const image = {
      width: Math.max(0, Math.round(width)),
      height: Math.max(0, Math.round(height)),
    };
    const warningCodes = result.warningCodes.filter(code => code !== 'low_resolution');
    if (Math.max(image.width, image.height) < 1200) warningCodes.push('low_resolution');
    return { ...result, image, warningCodes };
  }

  return Object.freeze({ EXIF_TAGS, readPhotoMetadata, withDecodedDimensions });
}));
